import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import classNames from 'classnames/bind';
import TogglePasswordButton from 'templates/LoginPageData/components/TogglePasswordButton/TogglePasswordButton';
import { REGEXP_EMAIL } from 'common/helpers/valdiation';
import styles from 'templates/LoginPageData/LoginPageData.module.scss';
import PageLink from 'components/PageLink/PageLink';
import { fetcher } from 'app/http';
import MobileRegistration from 'components/MobileRegisteration/MobileRegisteration';
import MediaQuery from 'react-responsive';
import ServerContext from 'common/utils/serverContext';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { formProgression, formError } from 'common/helpers/Gtm';
import TagManager from 'react-gtm-module';

type Props = {
  Model: any;
  PageData: any;
  cleanForm?: boolean;
  onSubmit: (data: any) => void;
  onShowResetForm?: () => void;
};

const LoginForm = ({
  Model,
  PageData,
  cleanForm,
  onSubmit,
  onShowResetForm,
}: Props) => {
  const [isAutoCompleted, setIsAutoCompleted] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const { isMobile } = useContext(ServerContext);
  const values = isMobile ? {} : { deviceWidth: 1200 };
  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid, isDirty, isSubmitSuccessful, errors },
    getFieldState,
  } = useForm({
    defaultValues: {
      FormModel: {
        EmailAddress: '',
        Password: '',
        Mode: Model?.FormModel?.Mode,
        AuthorizeUrl: Model?.FormModel?.AuthorizeUrl,
        RedirectUrl: Model?.FormModel?.RedirectUrl,
        ProfileId: Model?.FormModel?.ProfileId,
        IsDisableRecaptcha : Model?.FormModel?.IsDisableRecaptcha,
        RecaptchaResponseToken: ''
      },
    },
    mode: 'all',
  });
  const disabledSubmit = useMemo(() => {
    // Disable on submit
    if (isSubmitSuccessful) {
      return true;
    }
    // Enable on autocomplete
    if (!isDirty && isAutoCompleted) {
      return false;
    }
    // Enable when is filled and is valid
    if (isDirty && isValid) {
      return false;
    }
    // Disable by default
    return true;
  }, [isAutoCompleted, isDirty, isValid, isSubmitSuccessful]);

  useEffect(() => {
    
    var tagManagerArgs: any;

    if(errors.FormModel?.EmailAddress)
    { 
      formError('Login Form','Email Address','error_email_is_not_recognized_or_registered')           
    }

    if(errors.FormModel?.Password)
    {            
      formError('Login Form','Password','Password is required') 
    }      
  }, [errors]);

  
  const emailAddressState = getFieldState('FormModel.EmailAddress');
  const passwordState = getFieldState('FormModel.Password');
  const { executeRecaptcha } = useGoogleReCaptcha(); // Recaptcha v3

  const handleLoginForm = async (data: any) => {
    if (Model.FormModel.IsDisableRecaptcha) {
      onSubmit(data);
    } else {
      if (!executeRecaptcha) {
        return;
      }
      const token = await executeRecaptcha();

      if (token) {
        data.FormModel = {
          ...data.FormModel,
          RecaptchaResponseToken: token,
        };
        onSubmit(data);
      } else {
        onSubmit(data);
      }
    }
  };

  const appleIdClick = async () => {
    const response = await fetcher.get(Model?.AppleUrl);

    if (response?.data?.RedirectUrl) {
      window.location.href = response?.data?.RedirectUrl;
    }
  };
  const googleIdClick = async () => {
    const response = await fetcher.get(Model?.GoogleUrl);

    if (response?.data?.RedirectUrl) {
      window.location.href = response?.data?.RedirectUrl;
    }
  };

  useEffect(() => {
    if (cleanForm) {
      reset();
    }
  }, [cleanForm]);

  useEffect(() => {
    // Enable submit button on autocomplete
    document.addEventListener('onautocomplete', () => {
      setIsAutoCompleted(true);
    });
    return () => {
      document.removeEventListener('onautocomplete', () => { setIsAutoCompleted(false)});
    };
  }, []);

  return (
    <>
      <MediaQuery maxWidth={1024} values={values}>
        <MobileRegistration
          RegisterMyAccountLabel={PageData?.RegisterMyAccountLabel}
          redirectionUrl={Model?.RegisterUrl}
          RegisterAccountText={PageData?.RegisterAccountText}
        />
      </MediaQuery>

      <form className={styles.wrapper} onSubmit={handleSubmit(handleLoginForm)}>
        <div id="LoginContainer">
          <div className="auth0-lock-container">
            <div className="auth0-lock-heading">
              {PageData?.LoginToYourAccountLabel}
            </div>
            {Model?.ShowedInvalidLoginPassword && (
              <span className="password-not-recognized">
                {PageData?.InvalidEmailOrPasswordLabel}
              </span>
            )}
            {Model?.ShowedUserAlreadyExists && (
              <span className="password-not-recognized">
                {PageData?.UserAlreadyExistsLabel}
              </span>
            )}
            {Model?.ShowAccountBlocked && (
              <span className="password-not-recognized">
                {PageData?.AccountBlockedMessage}
              </span>
            )}

            <div
              className={classNames(
                'auth0-lock-input-wrap error-email',
                errors.FormModel?.EmailAddress && 'error-message',
                emailAddressState.isDirty &&
                  !emailAddressState.invalid &&
                  'valid',
              )}
            >
              <label>{PageData.EmailAddress}</label>
              <div className="invertable-mark"></div>
              <input
                {...register('FormModel.EmailAddress', {
                  required:PageData?.EmailFieldRequiredMessage,
                  pattern: {
                    value: REGEXP_EMAIL,
                    message: PageData?.EmailErrorMessage,
                  },
                })}
                type="text"
                id="1-email"
                inputMode="email"
                className="auth0-lock-input"
                placeholder={PageData?.EmailAddressPlaceHolder}
                autoComplete="off"
                autoCapitalize="off"
                aria-label="Email"
                aria-invalid="true"
                name="FormModel.EmailAddress"
                onBlur={(e)=>formProgression(e,'Login','emailAddress')}
              />
              {errors.FormModel?.EmailAddress && (
                <div className="error">
                  {errors.FormModel?.EmailAddress?.message}
                </div>
              )}
            </div>

            <div
              className={classNames(
                'auth0-lock-input-wrap auth0-lock-show-password',
                passwordState.isDirty && !passwordState.invalid && 'valid',
              )}
            >
              <div className={styles.label}>
                <label>{PageData.Password}</label>
                <TogglePasswordButton
                  isShowed={showPassword}
                  className={styles.togglePassword}
                  onChange={() => setShowPassword(!showPassword)}
                />
              </div>
              <input
                {...register('FormModel.Password', {
                  required: 'Password is required',
                })}
                type={showPassword ? 'text' : 'password'}
                id="1-password"
                className="auth0-lock-input"
                aria-label="Password"
                aria-invalid="true"
                placeholder={PageData?.PasswordPlaceHolder}
                onBlur={(e)=>formProgression(e,'Login','password')}
              />
              {errors.FormModel?.Password && (
                <span className="error">
                  {errors.FormModel.Password.message}
                </span>
              )}
            </div>
            <input
              type="hidden"
              {...register('FormModel.IsDisableRecaptcha')} 
              value={Model?.FormModel?.IsDisableRecaptcha}
            />
             <input
              type="hidden"
              {...register('FormModel.Mode')} 
              value={Model?.FormModel?.Mode}
            />
            <div className="auth0-lock-button-submit-panel">
              <button
                className="Button u-block w-100"
                type="submit"
                disabled={disabledSubmit}
              >
                {PageData?.LoginButtonLabel}
              </button>
              {onShowResetForm && (
                <button
                  type="button"
                  onClick={onShowResetForm}
                  className="forgotbutton"
                >
                  {PageData?.ForgotPasswordLabel}
                </button>
              )}
            </div>
          </div>
          <div className="auth0-lock-social-buttons-wrapper">
            <div className="auth0-lock-social-buttons-container">
              <div className="auth0-lock-social-buttons-container__heading">
                <strong>{PageData?.ORLabel}</strong>{' '}
                {PageData?.UseSocialLoginLabel}
              </div>

              <button                
                className="auth0-lock-social-button auth0-lock-social-big-button"
                data-provider="apple"
                type="button"
                onClick={appleIdClick}
              >
                <div className="auth0-lock-social-button-icon"></div>
                <div className="auth0-lock-social-button-text">
                  {PageData?.ContinueWithAppleIdLabel}
                </div>
              </button>
              <button              
                className="auth0-lock-social-button auth0-lock-social-big-button"
                data-provider="google-oauth2"
                type="button"
                onClick={googleIdClick}
              >
                <div className="auth0-lock-social-button-icon"></div>
                <div className="auth0-lock-social-button-text">
                  {PageData?.ContinueWithGoogleLabel}
                </div>
              </button>
            </div>
            <MediaQuery minWidth={1024} values={values}>
              <div className="auth0-lock-registration-panel-wrapper">
                <div className="auth0-lock-registration-panel">
                  <div className="auth0-lock-registration-panel__headertext">
                    {PageData?.RegisterMyAccountLabel}
                  </div>
                  <div className="auth0-lock-registration-panel__content">
                    {PageData?.RegisterMyAccountInfoText}
                  </div>
                  <PageLink
                    url={Model?.RegisterUrl}
                    text={PageData?.RegisterAccountText}
                    className="Button u-block Button--ghost"
                  />
                </div>
              </div>
            </MediaQuery>
          </div>
        </div>
      </form>
      {Model?.showRecaptchaError && (
        <div className="form-text text-danger">
          {PageData?.ReCaptchaErrorMessage}
        </div>
      )}
    </>
  );
};

export default LoginForm;
